import { z } from 'zod';
import { classDisplay, totalLevel } from './character-access';
// =============================================================================
// ABILITY SCORES
// =============================================================================
/**
 * Ability scores, keyed by `abilityStorageKey()` — the ability's name,
 * lowercased. 5e's Strength lands on `strength`, Doomsworn's Attunement on
 * `attunement`, and a Trophy character's four land wherever Trophy names them.
 *
 * THE SIX ARE A CONVENTION NOW, NOT A REQUIREMENT. They used to be declared
 * fields with a catchall for extras, which said that every character in every
 * system has Strength. A classless four-ability character has no Strength, and
 * a record is the shape that can hold one.
 *
 * WHAT THAT COSTS, STATED PLAINLY: `scores.strength` still type-checks as a
 * number, because this project does not enable `noUncheckedIndexedAccess`. The
 * compiler no longer guards the six, and reading a missing ability yields
 * undefined at runtime rather than an error at build time. That trade is taken
 * deliberately — the alternative rippled `number | undefined` through roughly a
 * hundred arithmetic sites whose real problem is different: 5e code reading
 * 5e abilities is correct, and the bug is 5e code RUNNING for a non-5e
 * character. That is fixed by asking the system which abilities it has
 * (`sheetAbilities`, `toSystemAbilities`), not by making every read optional.
 *
 * Use `abilityScore()` where absence is a real possibility and a number is
 * needed anyway.
 */
export const AbilityScoresSchema = z.record(z.string(), z.number().int().min(1).max(30));
export function getModifier(score) {
    return Math.floor((score - 10) / 2);
}
// =============================================================================
// CHARACTER CLASS
// =============================================================================
export const CharacterClassSchema = z.object({
    name: z.string(), // "Fighter", "Wizard", etc.
    level: z.number().int().min(1).max(20),
    subclass: z.string().optional(), // "Champion", "Evocation", etc.
    hitDie: z.number().int(), // 6, 8, 10, or 12
});
// =============================================================================
// CHARACTER
// =============================================================================
export const CharacterSchema = z.object({
    id: z.string().uuid(),
    // Identity
    name: z.string(),
    playerName: z.string().optional(), // For PCs
    race: z.string(),
    background: z.string().optional(),
    alignment: z.string().optional(),
    /**
     * Classes, for systems that have them. Empty for a classless system.
     *
     * Was `.min(1)`, which made "every character belongs to a class" a property
     * of the storage layer rather than of D&D. Read it through `totalLevel()` and
     * `classDisplay()` — a bare `.reduce` over this crashes on a Trophy
     * character, and there were two dozen of those.
     */
    classes: z.array(CharacterClassSchema).default([]),
    // Core stats
    abilityScores: AbilityScoresSchema,
    /**
     * 5e-family fields. Optional because a system can decline to have them: a
     * Cairn-chassis character has no proficiency bonus, and a Trophy character
     * has neither armour class nor hit dice.
     *
     * They stay at the top level rather than moving into `systemState` because
     * every one of them is read in dozens of places, and an optional field plus
     * an accessor gets a non-5e character stored today at a fraction of the risk.
     * Once the accessors below are the only readers, relocating the bytes is a
     * one-file change; doing it first would have been a hundred-file one.
     */
    proficiencyBonus: z.number().int().min(2).max(6).optional(),
    armorClass: z.number().int().min(1).optional(),
    hitDice: z.string().optional(), // "3d10" for level 3 fighter
    hitDiceRemaining: z.number().int().optional(),
    // Health is universal — every system that has bodies has a way to hurt them.
    maxHp: z.number().int().min(1),
    currentHp: z.number().int(),
    tempHp: z.number().int().default(0),
    // Speed & movement
    speed: z.number().int().default(30),
    // Saves & skills
    savingThrowProficiencies: z.array(z.string()).default([]),
    skillProficiencies: z.array(z.string()).default([]),
    skillExpertise: z.array(z.string()).default([]),
    // Equipment
    equipment: z.array(z.string()).default([]),
    // Features & traits
    features: z.array(z.object({
        name: z.string(),
        source: z.string(), // "Fighter 1", "Human", "Soldier"
        description: z.string(),
    })).default([]),
    // Spellcasting (optional)
    spellcasting: z.object({
        ability: z.enum(['intelligence', 'wisdom', 'charisma']),
        spellSlots: z.record(z.string(), z.number()).default({}), // {"1": 4, "2": 3}
        spellSlotsUsed: z.record(z.string(), z.number()).default({}),
        knownSpells: z.array(z.string()).default([]),
        preparedSpells: z.array(z.string()).default([]),
    }).optional(),
    // Resources
    resources: z.array(z.object({
        name: z.string(),
        max: z.number().int(),
        current: z.number().int(),
        rechargeOn: z.enum(['short_rest', 'long_rest', 'dawn', 'never']),
    })).default([]),
    // Notes
    personalityTraits: z.string().optional(),
    ideals: z.string().optional(),
    bonds: z.string().optional(),
    flaws: z.string().optional(),
    backstory: z.string().optional(),
    notes: z.string().optional(),
    playerNotes: z.string().optional(), // Player's own campaign notes (private to them)
    // Appearance
    avatarUrl: z.string().optional(), // Can be URL or relative path like /assets/...
    tokenImageUrl: z.string().optional(), // Custom token image (player-uploaded)
    appearance: z.string().optional(), // Visual description for AI sprite generation (skin, hair, build, outfit)
    tokenColor: z.string().optional(),
    badgeId: z.string().optional(), // Badge identifier for player color choice
    badgeIcon: z.string().optional(), // Icon identifier for player icon choice
    /** Persisted HD-2D iso sprites (4 facings → Storage URLs). Seeds iso cache on load. */
    isoSprites: z.object({
        south: z.string().optional(),
        north: z.string().optional(),
        east: z.string().optional(),
        west: z.string().optional(),
    }).optional(),
    /**
     * Per-system persistent state, keyed by system id (e.g. `doomsworn`).
     *
     * Core stays deliberately ignorant of the shape inside: each system package
     * owns and validates its own slot. That keeps a system's mechanics out of the
     * 5e-shaped character schema, and means a new system needs no migration —
     * `machina_characters.data` stores this whole object verbatim, so anything
     * added here persists for free with the character's existing RLS.
     */
    systemState: z.record(z.unknown()).optional(),
    // Metadata
    campaignId: z.string().uuid().optional(),
    isNpc: z.boolean().default(false),
    npcControlMode: z.enum(['gm', 'ai', 'ai_suggest', 'player']).default('gm'),
    npcPersonality: z.string().optional(),
    npcTactics: z.string().optional(),
    npcTitle: z.string().optional(), // e.g. "Cleric of Ubtao"
    npcFactionColor: z.string().optional(), // e.g. '#d4a574'
    npcCampaignNpcId: z.string().uuid().optional(),
    absentThisSession: z.boolean().default(false), // Player is out this session
    echoMadness: z.number().int().default(0).optional(), // Echo Knight hidden mechanic
    reputation: z.number().int().default(0).optional(), // Skolnit — social reputation score
    mojo: z.number().int().default(0).optional(), // Mad Juju — mojo score
    inventorInspiration: z.number().int().default(0).optional(), // Dax — inventor's inspiration score
    concentratingOn: z.string().optional(), // Current concentration spell name
    xp: z.number().int().default(0), // Experience points
    pendingLevelUp: z.boolean().default(false), // True when XP crosses next threshold
    createdAt: z.date(),
    updatedAt: z.date(),
});
// =============================================================================
// PARTY
// =============================================================================
export const PartySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    campaignId: z.string().uuid(),
    characterIds: z.array(z.string().uuid()),
    partyLevel: z.number().int().optional(), // Average or explicit
    // Shared resources
    partyGold: z.number().default(0),
    sharedInventory: z.array(z.string()).default([]),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// =============================================================================
// SYSTEM-CONTROLLED NPC
// =============================================================================
/** Canonical playerName for NPCs run by Machina's AI automation ("The Deep"). */
export const SYSTEM_PLAYER_NAME = 'The Deep';
/** Returns true if a character is an AI-controlled NPC managed by Machina. */
export function isSystemControlled(char) {
    return char.isNpc && (char.npcControlMode === 'ai' || char.playerName === SYSTEM_PLAYER_NAME);
}
export function getCharacterSummary(char) {
    return {
        id: char.id,
        name: char.name,
        playerName: char.playerName,
        race: char.race,
        classDisplay: classDisplay(char),
        level: totalLevel(char),
        currentHp: char.currentHp,
        maxHp: char.maxHp,
        armorClass: char.armorClass,
        avatarUrl: char.avatarUrl,
        tokenImageUrl: char.tokenImageUrl,
        tokenColor: char.tokenColor,
        badgeId: char.badgeId,
        badgeIcon: char.badgeIcon,
        isNpc: char.isNpc,
        npcControlMode: char.isNpc ? char.npcControlMode : undefined,
        absentThisSession: char.absentThisSession,
        xp: char.xp ?? 0,
        pendingLevelUp: char.pendingLevelUp ?? false,
    };
}
// =============================================================================
// FACTORY
// =============================================================================
export function createCharacter(params) {
    const now = new Date();
    return {
        id: crypto.randomUUID(),
        name: params.name,
        race: params.race,
        // No default class. This used to hand every character a level 1 Fighter,
        // which is how a Trophy character would have arrived in the database with a
        // hit die and a class it never had. A caller that wants a Fighter says so.
        classes: params.classes ?? [],
        abilityScores: params.abilityScores || {
            strength: 10,
            dexterity: 10,
            constitution: 10,
            intelligence: 10,
            wisdom: 10,
            charisma: 10,
        },
        proficiencyBonus: params.proficiencyBonus,
        armorClass: params.armorClass,
        maxHp: params.maxHp || 10,
        currentHp: params.currentHp ?? params.maxHp ?? 10,
        tempHp: params.tempHp || 0,
        hitDice: params.hitDice,
        hitDiceRemaining: params.hitDiceRemaining,
        speed: params.speed || 30,
        savingThrowProficiencies: params.savingThrowProficiencies || [],
        skillProficiencies: params.skillProficiencies || [],
        skillExpertise: params.skillExpertise || [],
        equipment: params.equipment || [],
        features: params.features || [],
        spellcasting: params.spellcasting,
        resources: params.resources || [],
        personalityTraits: params.personalityTraits,
        ideals: params.ideals,
        bonds: params.bonds,
        flaws: params.flaws,
        backstory: params.backstory,
        notes: params.notes,
        avatarUrl: params.avatarUrl,
        tokenColor: params.tokenColor,
        badgeId: params.badgeId,
        badgeIcon: params.badgeIcon,
        playerName: params.playerName
            ?? (params.isNpc && params.npcControlMode === 'ai' ? SYSTEM_PLAYER_NAME : undefined),
        background: params.background,
        alignment: params.alignment,
        isNpc: params.isNpc || false,
        npcControlMode: params.npcControlMode || 'gm',
        npcPersonality: params.npcPersonality,
        npcTactics: params.npcTactics,
        npcCampaignNpcId: params.npcCampaignNpcId,
        campaignId: params.campaignId,
        absentThisSession: params.absentThisSession || false,
        xp: params.xp ?? 0,
        pendingLevelUp: params.pendingLevelUp ?? false,
        createdAt: params.createdAt || now,
        updatedAt: params.updatedAt || now,
    };
}
//# sourceMappingURL=character-types.js.map